import { useEffect } from 'react';
import { useStore } from '@nanostores/react';

import { useUserPhrQuery } from '@/entities/user-phr';
import { logger } from '@/shared';
import { pivotByDateKey } from '@/shared/lib/table';
import {
  $patientWorkspaceSearchTarget,
  clearPatientWorkspaceSearchTarget,
} from '@/shared/stores/ui';
import { EmptyState } from '@/shared/ui/primitives';
import { Skeleton, SkeletonRow } from '@/shared/ui/primitives';

import { phrTableConfigs } from '../model/phrTableConfig';
import { PhrTable } from './PhrTable';

const EMPTY_STATE_BY_TABLE_KEY: Record<string, { title: string; description: string }> = {
  conditions: {
    title: 'No conditions',
    description: 'No conditions are available in the patient health record.',
  },
  labs: {
    title: 'No labs',
    description: 'No lab results are available in the patient health record.',
  },
  medications: {
    title: 'No medications',
    description: 'No medications are available in the patient health record.',
  },
  notes: {
    title: 'No notes',
    description: 'No notes are available in the patient health record.',
  },
  procedures: {
    title: 'No procedures',
    description: 'No procedures are available in the patient health record.',
  },
  vitals: {
    title: 'No vitals',
    description: 'No vitals are available in the patient health record.',
  },
};

interface PhrTableTabProps {
  userId: string;
  tableKey: string;
}

export function PhrTableTab({ userId, tableKey }: PhrTableTabProps) {
  const phr = useUserPhrQuery(userId);
  const searchTarget = useStore($patientWorkspaceSearchTarget);
  const resourceId = `phr:${tableKey}`;
  const highlightedRowId =
    searchTarget?.tabId === resourceId && searchTarget.resource === resourceId
      ? searchTarget.resultId
      : undefined;
  const highlightedText =
    searchTarget?.tabId === resourceId && searchTarget.resource === resourceId
      ? searchTarget.query
      : undefined;

  useEffect(() => {
    if (!phr.isSuccess) return;
    const table = phr.data.phr[tableKey as keyof typeof phr.data.phr];
    if (!Array.isArray(table)) return;
    logger.info('phr table loaded', { userId, tableKey, rowCount: table.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phr.data, phr.isSuccess, tableKey, userId]);

  if (phr.isLoading) {
    return (
      <div className="space-y-3" aria-label="Loading">
        <Skeleton className="h-9 w-64" />
        <div className="space-y-2">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      </div>
    );
  }

  if (!phr.isSuccess) {
    return null;
  }

  const table = phr.data.phr[tableKey as keyof typeof phr.data.phr];
  if (!Array.isArray(table) || table.length === 0) {
    const empty = EMPTY_STATE_BY_TABLE_KEY[tableKey] ?? {
      title: 'No data',
      description: 'No patient health record data is available for this tab.',
    };
    return <EmptyState title={empty.title} description={empty.description} />;
  }

  const config = phrTableConfigs[tableKey];
  const rows =
    config?.transform === 'pivotByStartDate'
      ? pivotByDateKey(table as object[], {
          nameKey: 'name',
          startDateKey: 'start',
          valueKey: 'value',
          ...(config.transformOptions as Record<string, string> | undefined),
        })
      : (table as object[]);

  return (
    <PhrTable
      rows={rows}
      config={config}
      highlightedRowId={highlightedRowId}
      highlightedText={highlightedText}
      onHighlightConsumed={clearPatientWorkspaceSearchTarget}
    />
  );
}
